import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, RefreshCcw, Cpu, CheckCircle2, ShieldCheck, HelpCircle } from "lucide-react";
import GameInstructionsModal from "./ui/GameInstructionsModal";

const PerformanceWorkshop = ({ id = "workshop" }) => {
  const [gameState, setGameState] = useState("start");
  const [optimization, setOptimization] = useState({ images: 50, bundle: 50, cache: 50 });
  const [target, setTarget] = useState({ images: 90, bundle: 20, cache: 95 });
  const [loadTime, setLoadTime] = useState(5.4);
  const [score, setScore] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);

  const initGame = () => {
    setTarget({
      images: Math.floor(Math.random() * 40) + 60,
      bundle: Math.floor(Math.random() * 40) + 10,
      cache: Math.floor(Math.random() * 40) + 60
    });
    setOptimization({ images: 50, bundle: 50, cache: 50 });
    setGameState("playing");
  };

  useEffect(() => {
    // Calculate simulated load time
    const baseTime = 10;
    const reduction = (optimization.images * 0.4) + ((100 - optimization.bundle) * 0.4) + (optimization.cache * 0.2);
    const newTime = Math.max(0.4, (baseTime - (reduction / 10))).toFixed(1);
    setLoadTime(newTime);

    if (gameState === "playing" && newTime < 1.0) {
      setGameState("success");
      setScore(s => s + 1);
    }
  }, [optimization, gameState]);

  return (
    <section id={id} className="py-24 relative overflow-hidden bg-[#050505]">
      <div className="container-xl px-6">
        <div className="flex flex-col mb-12">
          <div className="text-left mb-8">
            <Kicker index="DEV-01" label="Performance Lab" />
          </div>
          <div className="text-center">
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-[var(--cream)]">
              Speed <span className="italic gold-text">Optimizer</span>
            </h2>
            <p className="mt-4 text-[var(--cream-muted)] text-sm uppercase tracking-[0.2em] max-w-lg mx-auto">
              Tweak the infrastructure to achieve sub-second load times.
            </p>
            <button 
              onClick={() => setShowInstructions(true)}
              className="mt-6 flex items-center gap-2 text-[var(--gold)]/60 hover:text-[var(--gold)] transition-colors mx-auto text-[10px] font-impact tracking-widest uppercase"
            >
              <HelpCircle size={14} />
              How to Play
            </button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center bg-[#0d0d0d] p-8 md:p-16 rounded-[40px] border border-[var(--rule)] relative shadow-2xl">
          
          {/* Performance Monitor */}
          <div className="relative aspect-[4/3] md:aspect-video bg-black rounded-3xl overflow-hidden border border-[var(--rule)] flex flex-col items-center justify-center p-12">
            <div className="absolute top-4 left-6 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-[10px] text-green-500 font-mono uppercase tracking-widest">System Live</span>
            </div>

            <motion.div 
              animate={{ scale: loadTime < 1.0 ? 1.1 : 1 }}
              className={`text-7xl md:text-9xl font-impact transition-colors duration-500 ${loadTime < 1.0 ? "text-[var(--gold)]" : "text-white"}`}
            >
              {loadTime}s
            </motion.div>
            <div className="text-[var(--muted)] font-display italic text-xl mt-4">Load Time</div>

            <div className="absolute bottom-8 inset-x-12 flex justify-between">
               <div className="h-1 flex-1 bg-white/5 rounded-full overflow-hidden mr-4">
                 <motion.div 
                   animate={{ width: `${(10 - loadTime) * 10}%` }}
                   className="h-full bg-[var(--gold)]" 
                 />
               </div>
            </div>
          </div>

          {/* Controls */}
          <div className="relative z-40">
            {gameState === "start" ? (
              <div className="text-center py-10 flex flex-col items-center">
                <Cpu className="w-16 h-16 text-[var(--gold)] mb-8 opacity-50" />
                <h3 className="font-display text-3xl text-white mb-8 italic">Optimize for Speed?</h3>
                <button 
                  className="btn-primary px-12"
                  onClick={initGame}
                >
                  Enter Workshop
                </button>
              </div>
            ) : (
              <div className="space-y-12">
                <SliderControl label="Image Compression" value={optimization.images} onChange={(v) => setOptimization({...optimization, images: v})} color="#c9a961" />
                <SliderControl label="Bundle Size (Lower is better)" value={optimization.bundle} onChange={(v) => setOptimization({...optimization, bundle: v})} color="#c9a961" />
                <SliderControl label="Edge Caching" value={optimization.cache} onChange={(v) => setOptimization({...optimization, cache: v})} color="#c9a961" />
                
                <div className="p-6 bg-white/5 border border-white/10 rounded-2xl flex items-center gap-4">
                  <ShieldCheck className="text-[var(--gold)]" size={24} />
                  <div className="text-xs text-[var(--muted)] leading-relaxed">
                    Achieving sub-1s load time requires a perfect balance of compression and caching.
                  </div>
                </div>
              </div>
            )}
          </div>

          <AnimatePresence>
            {gameState === "success" && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/95 backdrop-blur-xl z-50 rounded-[40px] flex flex-col items-center justify-center p-12 text-center"
              >
                <CheckCircle2 className="text-[var(--gold)] w-20 h-20 mb-8" />
                <h3 className="font-display text-5xl text-white mb-4">PERFORMANCE <span className="italic gold-text">PEAK</span></h3>
                <p className="text-[var(--cream-muted)] mb-10">Your infrastructure is now world-class.</p>
                <button className="btn-primary px-12" onClick={initGame}>Next Challenge</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <GameInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="Speed Optimizer Lab"
        instructions={[
          { title: "Understand the Metrics", desc: "The central display shows the simulated 'Load Time' in seconds. Your goal is to get this number below 1.0s (the 'golden' standard)." },
          { title: "Tune the Sliders", desc: "Adjust Image Compression, Bundle Size, and Edge Caching. Higher compression and caching usually reduce load times." },
          { title: "Balance the Stack", desc: "Watch how different combinations affect the performance bar in the HUD. A low bundle size combined with high caching is often key." },
          { title: "Reach Peak Performance", desc: "Once you achieve sub-second load times, the system hits 'Performance Peak'. Try to hit this target across different randomized challenges." }
        ]}
      />
    </section>
  );
};

const SliderControl = ({ label, value, onChange, color }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-end">
      <label className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold)] font-impact">{label}</label>
      <span className="text-xl font-display text-white">{value}%</span>
    </div>
    <input 
      type="range" min="0" max="100" value={value}
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-full h-1 bg-[var(--rule)] appearance-none cursor-pointer accent-[var(--gold)] rounded-full outline-none"
    />
  </div>
);

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default PerformanceWorkshop;
